import React, { useState } from "react";
import { Box, Flex } from "theme-ui";
import moment from "moment-timezone";

import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import Checkbox from "components/forms/Checkbox";
import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";

import { acknowledgePo } from "api/PurchaseOrder";
import DisclosureBlock, { Disclosure } from "./DisclosureBlock";

/**
 * MASTER_PLAN.md §3.9 #11 (mockup 23 first panel) — first-view
 * acknowledgment modal.
 *
 * Renders a summary card (site / material / quantity / add-ons /
 * service / date / terms / total) + every disclosure block in order +
 * a sticky action row. Reject is a secondary action inside the modal —
 * it skips the acknowledgment (per §4.3: wrong-provider / out-of-area
 * rejections shouldn't require reading terms).
 *
 * SESSION 76: the modal is no longer a trap. "View full PO first" and
 * the header X dismiss it so the provider can read the actual document
 * underneath. The gate was never the modal — `acceptOrDeny` returns 409
 * `requiresAcknowledgment` until `termsAcknowledgedAt` is set, and
 * Overview re-opens this modal on that response. Blocking the document
 * only produced acknowledgments of a summary.
 *
 * Non-plan POs (empty disclosures array) still get the modal — one
 * extra click on first view. Universal audit trail was the Session 34
 * v3.3 reframe reason. Fallback if push-back comes: gate the mount on
 * `disclosures.length > 0` in the caller.
 *
 * SESSION 45 audit finding DRIFT-9 fix. Mockup 23 Panel 1 shows a
 * checkbox `I have reviewed the details and any applicable notes
 * above` between the disclosures and the accept button, with the CTA
 * labeled `Accept (check first)`. The pattern is deliberate —
 * acknowledgment is *stateful*, staff (or the provider) must actively
 * confirm they've read the notes rather than blur-clicking Accept.
 * Previous shipped version called `acknowledgePo` on a single click,
 * so whichever provider clicked fastest through a stack of notes
 * still recorded as "acknowledged" — exactly the failure mode the
 * mockup was designed against. The count of notes reviewed at
 * check-time is passed back so `AcceptanceBanner` can render the
 * mockup 23 Panel 2 audit line (`… 2 of 2 notes reviewed at that
 * time`).
 */
export default function AcknowledgmentModal({
  po,
  disclosures,
  onAcknowledged,
  onReject,
  onDismiss,
}: {
  po: any;
  disclosures: Disclosure[];
  onAcknowledged: (payload: {
    termsAcknowledgedAt: string | null;
    termsAcknowledgedBy: { id: string; name: string | null } | null;
    notesReviewedCount: number;
  }) => void;
  onReject: () => void;
  /** Close WITHOUT acknowledging so the provider can read the actual PO
   * document underneath. Safe by construction: the backend gates
   * Sent -> Accepted on `termsAcknowledgedAt` and Overview re-opens this
   * modal on the 409, so dismissing delays acceptance rather than
   * bypassing it. Trapping someone on a summary of a document they have
   * not been allowed to read is the wrong way to get an informed
   * acknowledgment. */
  onDismiss?: () => void;
}) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [reviewed, setReviewed] = useState(false);

  const projectName = po?.project?.projectName || "";

  // TERMS — the thing a provider most needs to see before accepting and
  // the one thing this modal never said. `Standard terms apply` told
  // them nothing: on an EMCOR-style account the client is Net 60, and a
  // provider who assumed 30 has mispriced their own cash flow. Sourced
  // the same way the PO document renders it (Overview.tsx), so the modal
  // and the document can never disagree.
  const termsDays = Number(po?.project?.client?.terms) || 30;
  const termsLine = `Net ${termsDays}`;

  // WHAT THE WORK IS. A provider cares about the material and the
  // service, not who the client is — the client name was removed from
  // this summary for that reason (Jack, 2026-09-14).
  //
  // Rendered BY TYPE rather than as one joined string. `purchase_order_item`
  // carries `type` and `unit`, so the shape is already in the data:
  //   product       / yds   -> the mulch
  //   addon-product / sqft  -> Filter Fabric and similar
  //   service       / -     -> delivery, installation
  // Quantity gets its own row per item because it is the number a crew
  // loads a truck against — burying it in parentheses after a long
  // description is how it gets missed.
  const flatItems: any[] = Array.isArray(po?.lineitems) ? po.lineitems.flat() : [];

  const fmtQty = (li: any): string | null => {
    const qty = Number(li?.quantity || 0);
    if (!qty) return null;
    const unit = (li?.unit || "").trim();
    const label =
      unit === "yds" ? "yards" : unit === "sqft" ? "sq ft" : unit === "ea" ? "" : unit;
    return `${qty.toLocaleString("en-US")}${label ? ` ${label}` : ""}`;
  };

  const itemName = (li: any): string =>
    [li?.name, li?.description].filter(Boolean).join(" — ");

  const products = flatItems.filter((li) => li?.type === "product");
  const addons = flatItems.filter((li) => li?.type === "addon-product");
  const services = flatItems.filter((li) => li?.type === "service");
  // Anything with an unrecognised type still has to appear — silently
  // dropping a line a provider is being paid for would be worse than an
  // ugly row.
  const others = flatItems.filter(
    (li) => !["product", "addon-product", "service"].includes(li?.type),
  );
  const siteAddr = po?.project?.location?.address;
  const siteLine = siteAddr
    ? [siteAddr.line1, siteAddr.city, siteAddr.state]
        .filter(Boolean)
        .join(", ")
    : projectName || "—";
  // DATE LADDER. A provider needs a date from the moment the PO lands,
  // not only once a crew is booked — and the label has to say how firm
  // it is, or an estimate reads as a commitment.
  //
  //   project.scheduleDate   -> "Scheduled for"        (firm, crew booked)
  //   po.serviceDate         -> "Target service date"  (estimated)
  //   project.serviceDate    -> "Target service date"
  //   quote.serviceDate      -> "Target service date"
  //   quoteRequest.serviceDate -> "Requested service date" (the client's ask)
  //
  // Coverage measured on production (98 POs raised in 2026):
  //   po.serviceDate alone ......... 79
  //   + project.scheduleDate ....... 57 (firm)
  //   + project.serviceDate ........ 95
  //   + quote / RFQ serviceDate .... 97
  // Falling back through all of them is the difference between a
  // provider seeing a date and seeing nothing on roughly one PO in five.
  //
  // Deliberately does NOT read `po.scheduleDate`: that column is
  // populated on 0 of 98 production POs.
  const scheduledAt = po?.project?.scheduleDate || null;
  const targetAt =
    po?.serviceDate || po?.project?.serviceDate || po?.quote?.serviceDate || null;
  // The client's originally requested date. Weakest rung, so it is
  // labelled differently — calling it a target would overstate it.
  const requestedAt =
    po?.quote?.quoteRequest?.serviceDate || po?.quoteRequest?.serviceDate || null;

  const dateLabel = scheduledAt
    ? "Scheduled for"
    : targetAt
      ? "Target service date"
      : requestedAt
        ? "Requested service date"
        : "Service date";
  const serviceDate = scheduledAt
    ? moment(scheduledAt).format("MMM D, YYYY")
    : targetAt
      ? `${moment(targetAt).format("MMM D, YYYY")} (estimated)`
      : requestedAt
        ? `${moment(requestedAt).format("MMM D, YYYY")} (not yet scheduled)`
        : "Not scheduled yet";
  const total = Array.isArray(po?.lineitems)
    ? po.lineitems.flat().reduce(
        (sum: number, li: any) =>
          sum + Number(li.cost || 0) * Number(li.quantity || 0),
        0,
      )
    : 0;

  const submit = async () => {
    if (!reviewed) {
      toast.add(
        "Please confirm you've reviewed the details and notes above before accepting.",
        "var(--theme-ui-colors-danger)",
      );
      return;
    }
    setSubmitting(true);
    const res = await acknowledgePo({ data: { id: po.id } });
    setSubmitting(false);

    if (res?.termsAcknowledgedAt !== undefined) {
      onAcknowledged({
        termsAcknowledgedAt: res.termsAcknowledgedAt,
        termsAcknowledgedBy: res.termsAcknowledgedBy || null,
        // Snapshot count of notes visible at check-time, for the audit
        // line on subsequent views. Non-plan POs record 0 rather than
        // suggesting notes existed.
        notesReviewedCount: disclosures.length,
      });
    } else {
      toast.add(
        res?.message || "Unable to record acknowledgment.",
        "var(--theme-ui-colors-danger)",
      );
    }
  };

  return (
    <Modal isOpen={true} size="md" centered toggle={onDismiss || (() => undefined)}>
      <ModalHeader toggle={(onDismiss as any) || (undefined as any)}>Acknowledgment Required</ModalHeader>
      <ModalBody>
        <Box sx={{ maxHeight: "65vh", overflowY: "auto", paddingRight: "4px" }}>
        {/* Summary card */}
        <Box
          sx={{
            background: "#F1EFE8",
            border: "1px solid",
            borderColor: "inputOutline",
            borderRadius: "8px",
            padding: "14px 16px",
            marginBottom: "16px",
          }}
        >
          <H5
            fontFamily={"Poppins"}
            fontWeight={600}
            fontSize={"14px"}
            margin={0}
            marginBottom={"10px"}
          >
            Purchase Order Summary
          </H5>
          <SummaryRow label="Site" value={siteLine} />

          {/* Material — name on one row, the quantity on its own. */}
          {products.map((li, idx) => (
            <Box key={`prod-${idx}`}>
              <SummaryRow label={idx === 0 ? "Material" : ""} value={itemName(li)} />
              {fmtQty(li) && <SummaryRow label="Quantity" value={fmtQty(li) as string} />}
            </Box>
          ))}

          {/* Add-ons — fabric is the common one, and its sq ft matters as
              much as the mulch yardage because the crew has to carry it. */}
          {addons.map((li, idx) => (
            <Box key={`addon-${idx}`}>
              <SummaryRow label={idx === 0 ? "Also included" : ""} value={itemName(li)} />
              {fmtQty(li) && <SummaryRow label="Coverage" value={fmtQty(li) as string} />}
            </Box>
          ))}

          {services.map((li, idx) => (
            <SummaryRow
              key={`svc-${idx}`}
              label={idx === 0 ? "Service" : ""}
              value={[itemName(li), fmtQty(li)].filter(Boolean).join(" · ")}
            />
          ))}

          {others.map((li, idx) => (
            <SummaryRow
              key={`other-${idx}`}
              label={idx === 0 ? "Other" : ""}
              value={[itemName(li), fmtQty(li)].filter(Boolean).join(" · ")}
            />
          ))}

          <SummaryRow label={dateLabel} value={serviceDate} />
          {/* Terms sits directly above Total on purpose: the amount and
              when it gets paid are one decision, not two. */}
          <SummaryRow label="Payment terms" value={termsLine} emphasize />
          <SummaryRow label="Total" value={formatMoney(total)} emphasize />
        </Box>

        {/* Disclosure blocks */}
        {disclosures.length === 0 ? (
          <Subtext
            fontSize={"12px"}
            fontFamily={"Poppins"}
            margin={0}
            marginBottom={"8px"}
            color={themevals.colors.dkgrey}
          >
            Payment terms are {termsLine} — you are paid approximately{" "}
            {termsDays} days after the client is invoiced for this work. Please
            review the summary above and acknowledge to continue.
          </Subtext>
        ) : (
          disclosures.map((d, idx) => (
            <DisclosureBlock
              key={`${d.type}-${idx}`}
              disclosure={d}
              collapsible={false}
            />
          ))
        )}

        {/* SESSION 45 DRIFT-9 — stateful review gate. Sits between the
         * disclosures and the accept action so the checkbox is next to
         * the content it acknowledges reading. Wording mirrors mockup
         * 23 Panel 1 verbatim. */}
        <Box
          sx={{
            marginTop: "16px",
            padding: "12px 14px",
            background: reviewed ? "#EAF3DE" : "#F1EFE8",
            border: "1px solid",
            borderColor: reviewed ? "#B4CE94" : "inputOutline",
            borderRadius: "6px",
            transition: "background 150ms ease, border-color 150ms ease",
          }}
        >
          <Checkbox
            checked={reviewed}
            onChange={() => setReviewed(!reviewed)}
            label={
              disclosures.length > 0
                ? "I have reviewed the details and any applicable notes above"
                : "I have reviewed the PO details above"
            }
            labelSize="13px"
          />
        </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        {/* Lets the provider read the actual PO document before
            committing. Overview's Accept re-opens this modal, so the
            acknowledgment is deferred, not skipped. */}
        {onDismiss && (
          <BasicButton type="button" outline scheme="primary" onClick={onDismiss}>
            View full PO first
          </BasicButton>
        )}
        <BasicButton type="button" outline scheme="dangerSecondary" onClick={onReject}>
          Reject PO
        </BasicButton>
        <BasicButton
          type="button"
          disabled={!reviewed}
          $submitting={submitting}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
          onClick={submit}
        >
          {reviewed ? "Accept" : "Accept (check first)"}
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
}

function SummaryRow({
  label,
  value,
  emphasize,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <Flex
      sx={{
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: "16px",
        marginBottom: "4px",
      }}
    >
      <Span
        fontFamily={"Poppins"}
        fontSize={"12px"}
        color={themevals.colors.dkgrey}
        sx={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
      >
        {label}
      </Span>
      {/* Values wrap rather than running off the card. A long line item
          description is the normal case, not the exception. */}
      <Span
        fontFamily={"Poppins"}
        fontSize={emphasize ? "14px" : "12px"}
        fontWeight={emphasize ? 600 : 400}
        sx={{ textAlign: "right", overflowWrap: "anywhere" }}
      >
        {value}
      </Span>
    </Flex>
  );
}

function formatMoney(n: number): string {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
